/**
 * Finishes the dataLayer that snippets/analytics-datalayer.liquid starts, and owns the
 * identity that links this storefront's on-site events to the checkout.
 *
 * Two properties cannot be resolved while the page is parsing, so the inline bootstrap
 * pushes its best guess and this file corrects it:
 *
 *   shop_session_id  mixpanel.get_distinct_id() — and Mixpanel is loaded by the GTM
 *                    container, so it does not exist until the container has run. Port of
 *                    the polling loop in app/root.jsx, same 100ms x 100 budget.
 *   user_type        <gnb-user-type> resolves it against api.kittor.com. The cached cookie
 *                    is already in the dataLayer; the `verified` event replaces it.
 *
 * WHY THE CART ATTRIBUTES LIVE HERE
 * Checkout is not part of this theme — Shopify renders it and no theme JS loads there — so
 * the only way an on-site session id reaches the checkout is through the cart, which the
 * Shopify server carries across that boundary. Web pixel 140214441 ("checkout complete
 * event") reads exactly two cart attributes and sends the first as Mixpanel's $device_id:
 *
 *   _shop_session_id   mixpanel.get_distinct_id().split('$device:')[1]
 *   _user_id           the KiTbetter user_id
 *
 * The Hydrogen storefront writes the same pair from four places (LinesAdd, the checkout
 * button, PDP buy-now, the fan-sign form). This file provides the values and the write; the
 * four call sites in this theme mirror those.
 *
 * ONE DIFFERENCE FROM HYDROGEN THAT MATTERS
 * Hydrogen writes through the Storefront API, which stores an empty attribute value. This
 * theme writes through the Ajax Cart API, where an empty value DELETES the attribute
 * (measured against /cart/update.js, 2026-09-07). The pixel does `shopSessionId.includes()`
 * outside its try block, so a missing attribute is a TypeError that aborts the whole
 * subscriber — losing the Mixpanel event and the complete_checkout dataLayer push with it.
 * That is why an unresolved id is written as PENDING_SHOP_SESSION_ID rather than '': the
 * attribute stays present, the pixel's `includes('-')` guard reads false, and the order is
 * recorded with an empty $device_id instead of not being recorded at all. Once the pixel
 * gains a `?.` there this only costs a placeholder value on the odd order; it is still the
 * safer write, so it stays.
 */
(function () {
  const SHOP_SESSION_STORAGE_KEY = 'shop_session_id';
  const MIXPANEL_POLL_INTERVAL_MS = 100;
  const MIXPANEL_POLL_LIMIT = 100;

  // Deliberately hyphen-free: the pixel treats a value containing '-' as a real Mixpanel
  // device id and would forward this one as $device_id, inventing a phantom user.
  const PENDING_SHOP_SESSION_ID = 'pending';

  const readCookie = (name) => {
    const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
    if (!match) return '';
    const raw = match.slice(name.length + 1);
    try {
      return decodeURIComponent(raw);
    } catch (error) {
      return raw;
    }
  };

  /**
   * The three values user_type is allowed to take: ANONYMOUS, ARTIST, NORMAL.
   *
   * <gnb-user-type> reports the raw user/info `role` and keeps it that way for the nav gates.
   * Analytics only distinguishes an artist from everyone else who is signed in, so anything
   * that is neither anonymous nor ARTIST becomes NORMAL — the point being that the vocabulary
   * stays closed to three values no matter what `role` turns out to carry. The inline
   * bootstrap in snippets/analytics-datalayer.liquid applies the identical reduction.
   */
  const normalizeUserType = (value) => {
    const type = (value || 'ANONYMOUS').toUpperCase();
    if (type === 'ANONYMOUS') return 'ANONYMOUS';
    return type === 'ARTIST' ? 'ARTIST' : 'NORMAL';
  };

  const readStoredShopSessionId = () => {
    try {
      return sessionStorage.getItem(SHOP_SESSION_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  const storeShopSessionId = (value) => {
    try {
      sessionStorage.setItem(SHOP_SESSION_STORAGE_KEY, value);
    } catch (error) {
      // Nothing to do: the value stays in memory for this page load only.
    }
  };

  /**
   * Reference: the isMixpanelLoaded effect in app/root.jsx. `$device:` is the prefix Mixpanel
   * puts on an anonymous distinct_id; the reference takes what follows it and falls back to
   * an empty string, which also means an *identified* distinct_id (no prefix) yields ''.
   * Kept as-is — the checkout pixel's $device_id is meant to be the device id.
   */
  const deriveShopSessionId = () => {
    const mixpanel = window.mixpanel;
    if (!mixpanel || !mixpanel.persistence || typeof mixpanel.get_distinct_id !== 'function') return '';

    const distinctId = mixpanel.get_distinct_id();
    if (!distinctId) return '';
    if (typeof distinctId !== 'string') return String(distinctId);
    return distinctId.split('$device:')[1] ?? '';
  };

  let shopSessionId = readStoredShopSessionId();
  const pendingResolvers = [];

  const settle = () => {
    while (pendingResolvers.length) pendingResolvers.shift()();
  };

  const applyShopSessionId = (value) => {
    if (!value || value === shopSessionId) return;
    shopSessionId = value;
    storeShopSessionId(value);
    window.dataLayer.push({ shop_session_id: value });
    settle();
  };

  const pollForMixpanel = () => {
    let checks = 0;
    const timer = setInterval(() => {
      checks += 1;
      const derived = deriveShopSessionId();
      if (derived) {
        clearInterval(timer);
        applyShopSessionId(derived);
        return;
      }
      if (checks >= MIXPANEL_POLL_LIMIT) {
        clearInterval(timer);
        settle();
      }
    }, MIXPANEL_POLL_INTERVAL_MS);
  };

  window.dataLayer = window.dataLayer || [];

  // A returning page load already has the id from sessionStorage, but the container still
  // has to load for the Mixpanel tag to fire, and the id can be rotated. Poll either way.
  pollForMixpanel();

  /**
   * <gnb-user-type> applies the cached cookie first and the API answer second, marking the
   * second with `verified`. Only the verified one is worth pushing — the cached value is
   * already in the dataLayer from the inline bootstrap.
   *
   * The verified detail carries the id and the artist pair straight from the user/info
   * response, which is why they are read from it rather than from cookies: artist_name has no
   * cookie to read, and taking all three from one payload keeps them consistent with each
   * other.
   *
   * Same invariant as the bootstrap — a non-anonymous user_type never goes out without the
   * user_id that identifies it. If the API confirmed a session but returned no id there is
   * nothing to attribute the events to, so the push is skipped rather than made half-valid.
   */
  document.addEventListener('gnb:usertype', (event) => {
    const detail = event.detail || {};
    if (!detail.verified) return;

    const userType = normalizeUserType(detail.userType);

    if (userType === 'ANONYMOUS') {
      window.dataLayer.push({ user_type: 'ANONYMOUS' });
      return;
    }

    const userId = detail.userId ?? readCookie('user_id');
    if (!userId) {
      console.error('[datalayer] user/info confirmed %s but returned no userId; not pushing.', userType);
      return;
    }

    const update = { user_type: userType, user_id: userId };

    // Contract from kitbetter-front src/components/Gtag/index.tsx: the pair travels together
    // and never goes out for an account that is not an artist. Gated on the reduced
    // user_type rather than on the raw role, so the pair can never contradict it.
    if (userType === 'ARTIST' && detail.artistId && detail.artistName) {
      update.artist_id = detail.artistId;
      update.artist_name = detail.artistName;
    }

    window.dataLayer.push(update);
  });

  window.KitAnalytics = {
    /** The resolved Mixpanel device id, or '' while the container is still loading. */
    shopSessionId: () => shopSessionId,

    userId: () => readCookie('user_id'),

    /**
     * The two attributes the checkout pixel reads. Merge into any /cart/update.js or
     * /cart/add.js attributes object rather than writing the keys by hand — the placeholder
     * rule above is the whole reason this is one function.
     */
    cartAttributes: () => ({
      _shop_session_id: shopSessionId || PENDING_SHOP_SESSION_ID,
      _user_id: readCookie('user_id'),
    }),

    /**
     * Resolves once the Mixpanel id is known, or after `timeoutMs`. Used by the checkout
     * button, which is the last chance to get a real id onto the cart: waiting a moment
     * there is cheaper than shipping an order that cannot be joined to its session.
     */
    whenIdentified(timeoutMs = 1500) {
      if (shopSessionId) return Promise.resolve(shopSessionId);
      return new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve(shopSessionId);
        };
        pendingResolvers.push(finish);
        setTimeout(finish, timeoutMs);
      });
    },

    /** Writes the identity onto the cart. Safe to call when the cart is empty. */
    syncCart() {
      const url = window.routes && window.routes.cart_update_url;
      if (!url) return Promise.resolve(null);

      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: this.cartAttributes() }),
      })
        .then((response) => response.json())
        .catch((error) => {
          console.error('[datalayer] cart identity sync failed.', error);
          return null;
        });
    },
  };
})();
